/*
 * pos-client.js  (ES5 + jQuery 3)
 * POS side. ONE socket per POS device serves ALL terminals, its customer screens
 * (terminal-screen.js) and the main-POS relay (terminal-main.js).
 *
 * Rules enforced here (the server enforces them again):
 *   - several terminals can be used at the same time (each payment has its own txId)
 *   - the SAME terminal accepts one transaction at a time
 *
 * jQuery events on document (data is the first argument after the event):
 *   terminal:terminals  [{id, online, busy}]
 *   terminal:tx         tx   (state: sending | accepted | in_terminal_app | no_ack | recovering | terminal_lost | unresolved)
 *   terminal:result     {tx, status, kind, data, reason}   FINAL
 *   terminal:rejected   {tx, code}                          request never started
 *   terminal:status     msg  (print / status replies)
 *
 * Start it only where it is needed, from any file, at any time:
 *   SocketHub.ensurePos({ features: ['print'] })      // see TerminalUi.js (also binds the UI)
 *   SocketHub.initPos({ features: [...], timeout })   // the bare channel, no UI
 *   -> jQuery promise: .done(function (pos) {}) .fail(function (code) {})
 *      codes: no_device_id | not_logged_in | replaced | timeout | stopped
 *   features: 'payments' (pay, refund, cancel, release, recovery of running payments)
 *             'print'    (print jobs)
 *             'screen'   (customer screens of this POS, see terminal-screen.js)
 *             'main'     (requests to / from the main POS, see terminal-main.js)
 *             'relay'    (general POS <-> POS messages, see terminal-relay.js)
 *                        ALWAYS on while the channel is open, so every connected POS is reachable
 *             'main' is also always on: every POS must hear when the main POS changes
 *             default when omitted: all
 *   Calling it again only ADDS features. The connection is never opened twice.
 *   A page that holds an unfinished payment always gets 'payments', so the result can still arrive.
 *
 * API: SocketHub.pos.pay({terminalId, amount, payload, meta}) -> {ok, txId} | {ok:false, code}
 *      SocketHub.pos.refund(...)  .cancel(txId)  .print(terminalId, html, {meta, segments})  .release(txId)
 *      SocketHub.pos.isAvailable(terminalId)  .list()  .active()  .check(terminalId)
 *      SocketHub.pos.connected()  .started()  .features()  .hasPersistedTxs()  .stop(force)
 *   error codes added here: not_initialized (initPos not called) | not_enabled (feature not requested)
 *
 * Module hook (for terminal-screen.js / terminal-main.js, not for page code):
 *   SocketHub._posModule(name, {onReady(welcome), onMessage(msg), onClose(), onStop(code), busy()})
 *     gets every '<name>.*' message while feature <name> is enabled
 *   SocketHub._posSend(type, payload) -> bool   SocketHub._posReady()   SocketHub._posHas(feature)
 */
(function (window, $) {
  'use strict';
  var T = window.SocketHub,
    M = T.MSG;
  var STORE = 'terminal.pos.activeTx',
    ACK_MS = 15000,
    UNRESOLVED_MS = 120000,
    MAX_AGE = 86400000;
  var sock = null,
    terminals = {},
    txs = {},
    timers = {},
    reconcile = null;
  var FEATURES = ['payments', 'print', 'screen', 'main', 'relay'];
  var ALWAYS = { relay: true, main: true }; // enabled on every POS connection, whatever the page asked for
  var features = {}, // enabled features: { payments: true, print: true, ... }
    waiters = [], // pending initPos promises
    stoppedReason = null;
  var modules = {}, // feature name (= message prefix) -> handlers
    lastWelcome = null;

  var jobs = {};
  var PRINT_RETRY_MS = [4000, 8000],
    PRINT_TIMEOUT_MS = 12000;
  var PRINT_FINAL = {
    dispatched: true,
    rejected: true,
    failed: true,
    error: true,
    timeout: true,
  };

  // ---- modules sharing this socket ------------------------------------------------
  function moduleCall(fn, arg) {
    $.each(modules, function (name, mod) {
      if (features[name] && typeof mod[fn] === 'function') {
        try {
          mod[fn](arg);
        } catch (e) {
          console.error(e);
        }
      }
    });
  }
  function modulesBusy() {
    var busy = false;
    $.each(modules, function (name, mod) {
      if (features[name] && typeof mod.busy === 'function' && mod.busy())
        busy = true;
    });
    return busy;
  }

  T._posModule = function (name, mod) {
    modules[name] = mod;
    if (features[name] && sock && sock.isReady() && mod.onReady)
      mod.onReady(lastWelcome || {});
  };
  T._posSend = function (type, payload) {
    return !!sock && sock.send(type, payload);
  };
  T._posReady = function () {
    return !!sock && sock.isReady();
  };
  T._posHas = function (name) {
    return !!sock && !!features[name];
  };

  function publicJob(j) {
    return {
      jobId: j.jobId,
      terminalId: j.terminalId,
      state: j.state,
      reason: j.reason,
      tries: j.tries,
      meta: j.meta,
    };
  }

  function setJobState(j, state, reason) {
    j.state = state;
    j.reason = reason || null;
    var pub = publicJob(j);
    if (PRINT_FINAL[state]) {
      $.each(j.timers, function (i, t) {
        clearTimeout(t);
      });
      delete jobs[j.jobId];
    }
    fire('print', pub);
    if (j.cb) {
      try {
        j.cb(pub);
      } catch (e) {
        console.error(e);
      }
    }
  }

  function sendPrint(j) {
    return sock.send(M.PRINT, {
      to: j.terminalId,
      jobId: j.jobId,
      document: j.html,
      segments: j.segments, // structured receipt (PAX); [] for HTML-only terminals
    });
  }

  function fire(name, data) {
    $(document).trigger('terminal:' + name, [data]);
  }

  function term(id) {
    id = String(id);
    if (!terminals[id]) terminals[id] = { id: id, online: false, busy: false };
    return terminals[id];
  }
  function list() {
    var a = [];
    $.each(terminals, function (id, t) {
      if (t.online) a.push($.extend({}, t));
    });
    return a;
  }
  function active() {
    var a = [];
    $.each(txs, function (id, tx) {
      a.push(tx);
    });
    return a;
  }
  function terminalHasActiveTx(id) {
    var found = false;
    $.each(txs, function (k, tx) {
      if (tx.terminalId === id) found = true;
    });
    return found;
  }

  // ---- persistence (a page reload must not lose a running payment) ----------
  function save() {
    try {
      localStorage.setItem(STORE, JSON.stringify(active()));
    } catch (e) {}
  }
  function load() {
    try {
      var arr = JSON.parse(localStorage.getItem(STORE) || '[]');
      $.each(arr, function (i, tx) {
        if (
          tx &&
          typeof tx.txId === 'string' &&
          Date.now() - (tx.startedAt || 0) < MAX_AGE
        )
          txs[tx.txId] = tx;
      });
    } catch (e) {}
  }

  // ---- tx bookkeeping ---------------------------------------------------------
  function touch(tx) {
    // persist + restart the "no sign of life" timer
    save();
    clearTimeout(timers[tx.txId]);
    timers[tx.txId] = setTimeout(function () {
      unresolved(tx.txId);
    }, UNRESOLVED_MS);
  }

  function forget(tx) {
    clearTimeout(timers[tx.txId]);
    delete timers[tx.txId];
    delete txs[tx.txId];
    save();
  }

  function finish(tx, res) {
    forget(tx);
    tx.state = 'done';
    fire('result', {
      tx: tx,
      status: res.status,
      kind: res.kind || tx.kind,
      data: res.data || null,
      reason: res.reason || null,
    });
    if (sock) sock.send(M.RESULT_ACK, { txId: tx.txId });
  }

  function unresolved(txId) {
    var tx = txs[txId];
    if (!tx) return;
    tx.state = 'unresolved';
    save();
    fire('tx', tx);
    if (typeof reconcile === 'function') {
      // ask YOUR backend about this txId; it must return a jQuery/native promise of {status, data} or null
      $.when(reconcile(tx)).done(function (r) {
        if (r && r.status && txs[txId]) {
          finish(tx, r);
          if (sock) sock.send(M.RELEASE, { txId: txId }); // free the terminal on the server
        }
      });
    }
  }

  // ---- server messages --------------------------------------------------------
  function onReady(w) {
    lastWelcome = w;
    terminals = {};
    $.each(w.peers || [], function (i, id) {
      term(id).online = true;
    });
    $.each(w.busy || [], function (i, id) {
      term(id).busy = true;
    });
    fire('terminals', list());
    recoverTxs();
    moduleCall('onReady', w);
    fire('ready', w);
    settleWaiters(true);
  }

  // after (re)connecting, transactions still on record are waited for again
  function recoverTxs() {
    $.each(txs, function (id, tx) {
      if (tx.state === 'sending' || tx.state === 'no_ack')
        tx.state = 'recovering';
      touch(tx);
      fire('tx', tx);
    });
  }

  function onClose() {
    terminals = {};
    fire('terminals', list()); // empty -> every terminal button gets disabled
    moduleCall('onClose');
  }

  function onMessage(m) {
    // screen.* and main.* go to their modules
    var prefix = m.type.slice(0, m.type.indexOf('.'));
    if (prefix !== 'terminal') {
      var mod = modules[prefix];
      if (mod && features[prefix] && typeof mod.onMessage === 'function') {
        try {
          mod.onMessage(m);
        } catch (e) {
          console.error(e);
        }
      }
      return;
    }

    var tx;
    switch (m.type) {
      case M.PEER:
        if (m.role === 'terminal') {
          term(m.id).online = !!m.online;
          fire('terminals', list());
        }
        break;
      case M.STATE:
        term(m.id).busy = !!m.busy;
        fire('terminals', list());
        break;
      case M.PROGRESS:
        tx = txs[m.txId];
        if (!tx) break;
        if (m.stage === 'handoff') tx.state = 'in_terminal_app';
        else if (m.stage === 'accepted' && tx.state !== 'in_terminal_app')
          tx.state = 'accepted';
        touch(tx);
        fire('tx', tx);
        break;
      case M.TX_UNKNOWN:
        // terminal dropped while the tx is active. After a handoff this is EXPECTED (payment app in front).
        tx = txs[m.txId];
        if (!tx) break;
        if (tx.state !== 'in_terminal_app') tx.state = 'terminal_lost';
        touch(tx);
        fire('tx', tx);
        break;
      case M.PAYMENT_RESULT:
        finish(
          txs[m.txId] || {
            txId: m.txId,
            terminalId: m.from,
            kind: m.kind || 'payment',
            orphan: true,
          },
          m
        );
        break;
      case M.BUSY:
        if (m.to) {
          term(m.to).busy = true;
          fire('terminals', list());
        }
        tx = txs[m.txId];
        if (tx) {
          forget(tx);
          fire('rejected', { tx: tx, code: 'terminal_busy' });
        }
        break;
      case M.ERROR:
        tx = m.txId ? txs[m.txId] : null;
        if (m.jobId && jobs[m.jobId]) {
          setJobState(jobs[m.jobId], 'error', m.code);
          break;
        }
        if (tx && tx.state === 'sending') {
          forget(tx);
          if (term(tx.terminalId).busy && !terminalHasActiveTx(tx.terminalId)) {
            term(tx.terminalId).busy = false; // undo our optimistic flag
            fire('terminals', list());
          }
          fire('rejected', { tx: tx, code: m.code });
        }
        break;
      case M.STATUS:
        if (m.jobId && jobs[m.jobId]) {
          var map = {
            print_dispatched: 'dispatched',
            print_rejected: 'rejected',
            print_failed: 'failed',
          };
          if (map[m.state]) setJobState(jobs[m.jobId], map[m.state], m.reason);
        }
        fire('status', m);
        break;
    }
  }

  // ---- public API -------------------------------------------------------------
  function request(kind, o) {
    var id = String(o.terminalId);
    if (!sock) return { ok: false, code: 'not_initialized' };
    if (!features.payments) return { ok: false, code: 'not_enabled' };
    if (!sock.isReady()) return { ok: false, code: 'channel_offline' };
    var t = terminals[id];
    if (!t || !t.online) return { ok: false, code: 'terminal_offline' };
    if (t.busy || terminalHasActiveTx(id))
      return { ok: false, code: 'terminal_busy' };

    var tx = {
      txId: T.newId(),
      terminalId: id,
      kind: kind,
      meta: o.meta || null,
      state: 'sending',
      startedAt: Date.now(),
    };
    txs[tx.txId] = tx;
    if (
      !sock.send(M.PAYMENT_REQUEST, {
        txId: tx.txId,
        to: id,
        kind: kind,
        payload: o.payload || null,
      })
    ) {
      delete txs[tx.txId];
      return { ok: false, code: 'send_failed' };
    }
    t.busy = true; // optimistic: only THIS terminal is blocked, others stay usable
    touch(tx);
    setTimeout(function () {
      var c = txs[tx.txId];
      if (c && c.state === 'sending') {
        c.state = 'no_ack';
        touch(c);
        fire('tx', c);
      }
    }, ACK_MS);
    fire('terminals', list());
    fire('tx', tx);
    return { ok: true, txId: tx.txId };
  }

  T.pos = {
    pay: function (o) {
      return request('payment', o);
    },
    refund: function (o) {
      return request('refund', o);
    },
    cancel: function (txId) {
      return !!txs[txId] && !!sock && sock.send(M.CANCEL, { txId: txId });
    },
    print: function (terminalId, html, extra, cb) {
      var id = String(terminalId),
        t = terminals[id];
      if (!sock) return { ok: false, code: 'not_initialized' };
      if (!features.print) return { ok: false, code: 'not_enabled' };
      if (!sock.isReady()) return { ok: false, code: 'channel_offline' };
      if (!t || !t.online) return { ok: false, code: 'terminal_offline' };
      if (t.caps && $.inArray('print', t.caps) === -1)
        return { ok: false, code: 'unsupported' };
      var segments = $.isArray(extra && extra.segments) ? extra.segments : [];
      html = typeof html === 'string' ? html : '';
      if (!html && !segments.length) return { ok: false, code: 'bad_request' };

      var j = {
        jobId: T.newId(),
        terminalId: id,
        state: 'sent',
        reason: null,
        tries: 1,
        html: html,
        segments: segments,
        meta: (extra && extra.meta) || null,
        cb: cb || null,
        timers: [],
      };
      jobs[j.jobId] = j;
      if (!sendPrint(j)) {
        delete jobs[j.jobId];
        return { ok: false, code: 'send_failed' };
      }

      // no answer: resend the SAME jobId (the terminal never prints it twice), then give up
      $.each(PRINT_RETRY_MS, function (i, delay) {
        j.timers.push(
          setTimeout(function () {
            var cur = jobs[j.jobId];
            if (cur && sock && sock.isReady()) {
              cur.tries++;
              sendPrint(cur);
            }
          }, delay)
        );
      });
      j.timers.push(
        setTimeout(function () {
          if (jobs[j.jobId]) setJobState(jobs[j.jobId], 'timeout');
        }, PRINT_TIMEOUT_MS)
      );

      fire('print', publicJob(j));
      return { ok: true, jobId: j.jobId };
    },
    // give up a tx after verifying it yourself; frees the terminal on the server
    release: function (txId) {
      var tx = txs[txId];
      if (!tx) return false;
      if (sock) sock.send(M.RELEASE, { txId: txId });
      forget(tx);
      return true;
    },
    isAvailable: function (id) {
      var t = terminals[String(id)];
      return !!t && t.online && !t.busy;
    },
    check: function (terminalId) {
      var id = String(terminalId || '');
      if (!sock) return { ok: false, code: 'not_initialized' };
      if (!sock.isReady()) {
        sock.wake(); // start reconnecting right away
        return { ok: false, code: 'channel_offline' };
      }
      if (!id) return { ok: false, code: 'no_terminal' };
      var t = terminals[id];
      if (!t || !t.online) return { ok: false, code: 'terminal_offline' };
      if (t.busy || terminalHasActiveTx(id))
        return { ok: false, code: 'terminal_busy' };
      return { ok: true };
    },
    list: list,
    active: active,
    connected: function () {
      return !!sock && sock.isReady();
    },
    started: function () {
      return !!sock;
    },
    features: function () {
      return $.extend({}, features);
    },
    hasPersistedTxs: hasPersistedTxs,
    // Close the channel. Refused (false) while a payment, print or main request runs, unless force.
    stop: function (force) {
      if (!sock) return true;
      if (
        !force &&
        (!$.isEmptyObject(txs) || !$.isEmptyObject(jobs) || modulesBusy())
      )
        return false;
      moduleCall('onStop', 'stopped');
      dropSock();
      features = {};
      fire('terminals', list());
      settleWaiters(false, 'stopped');
      return true;
    },
  };

  // ---- partial initialization ----------------------------------------------------

  function normalizeFeatures(f) {
    var out = {};
    if (f === undefined || f === null || f === 'all') f = FEATURES;
    if (typeof f === 'string') f = [f];
    $.each(f, function (i, k) {
      if ($.inArray(k, FEATURES) !== -1) out[k] = true;
    });
    return out;
  }
  T.normalizeFeatures = normalizeFeatures;

  function enableFeatures(want) {
    var before = $.extend({}, features);
    $.each(want, function (k) {
      features[k] = true;
    });
    if (features.payments && !before.payments) {
      load(); // bring back payments that were running before a reload / page change
      if (sock && sock.isReady()) recoverTxs();
    }
    // a module enabled on an already open connection starts at once
    if (sock && sock.isReady()) {
      $.each(modules, function (name, mod) {
        if (features[name] && !before[name] && mod.onReady)
          mod.onReady(lastWelcome || {});
      });
    }
  }

  function hasPersistedTxs() {
    if (!$.isEmptyObject(txs)) return true;
    try {
      var arr = JSON.parse(localStorage.getItem(STORE) || '[]');
      for (var i = 0; i < arr.length; i++) {
        if (
          arr[i] &&
          typeof arr[i].txId === 'string' &&
          Date.now() - (arr[i].startedAt || 0) < MAX_AGE
        )
          return true;
      }
    } catch (e) {}
    return false;
  }

  function waitReady(timeoutMs) {
    var d = $.Deferred();
    if (sock && sock.isReady()) return d.resolve(T.pos).promise();
    var w = { d: d };
    w.timer = setTimeout(function () {
      waiters = $.grep(waiters, function (x) {
        return x !== w;
      });
      d.reject('timeout'); // the connection keeps trying in the background
    }, timeoutMs || 8000);
    waiters.push(w);
    return d.promise();
  }

  function settleWaiters(ok, code) {
    var list_ = waiters;
    waiters = [];
    $.each(list_, function (i, w) {
      clearTimeout(w.timer);
      if (ok) w.d.resolve(T.pos);
      else w.d.reject(code);
    });
  }

  function dropSock() {
    if (sock) sock.destroy();
    sock = null;
    stoppedReason = null;
    terminals = {};
    lastWelcome = null;
  }

  /**
   * opts: features ('all' | ['payments','print','screen','main'] | 'print'), timeout (ms, default 8000),
   *       deviceId, url, tokenUrl (all optional: defaults come from SocketHub.config),
   *       reconcile(tx) (optional)
   * Safe to call any number of times: it opens the connection once and only adds features.
   * Returns a jQuery promise (resolved with SocketHub.pos once connected).
   */
  T.initPos = function (opts) {
    opts = opts || {};
    var deviceId = opts.deviceId || T.config.deviceId();
    if (!sock && !deviceId)
      return $.Deferred().reject('no_device_id').promise();
    if (sock && stoppedReason)
      return $.Deferred().reject(stoppedReason).promise();

    enableFeatures($.extend(normalizeFeatures(opts.features), ALWAYS));
    if (hasPersistedTxs()) enableFeatures({ payments: true });
    if (opts.reconcile) reconcile = opts.reconcile;

    if (!sock) {
      sock = T.createSocket({
        url: opts.url || T.config.url(),
        tokenUrl: opts.tokenUrl || T.config.tokenUrl,
        role: 'pos',
        deviceId: deviceId,
        onReady: onReady,
        onMessage: onMessage,
        onClose: onClose,
        onFatal: function () {
          // logged out: forget the socket so a later call can start again after login
          moduleCall('onStop', 'not_logged_in');
          dropSock();
          fire('terminals', list());
          fire('fatal', {});
          settleWaiters(false, 'not_logged_in');
        },
        onReplaced: function () {
          // another instance of this device took over: do not fight it
          stoppedReason = 'replaced';
          onClose();
          moduleCall('onStop', 'replaced');
          fire('replaced', {});
          settleWaiters(false, 'replaced');
        },
      });
      sock.start();
    }
    return waitReady(opts.timeout);
  };
})(window, jQuery);
